import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer
 * @property {HTMLElement} headerMenu
 * @property {HTMLElement} headerRowTop
 */

/** @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent */

/**
 * Keeps the theme's existing sticky and responsive header behavior.
 * The existing header-drawer component controls the mobile menu.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /** @type {number | null} */
  #menuDrawerHiddenWidth = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  #offscreen = false;
  #lastScrollTop = 0;

  /** @type {number | null} */
  #scrollRafId = null;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const borderBox = Array.isArray(entry.borderBoxSize)
      ? entry.borderBoxSize[0]
      : entry.borderBoxSize;
    const height = borderBox?.blockSize ?? this.offsetHeight;

    document.body.style.setProperty('--header-height', `${Math.round(height)}px`);

    if (
      this.#menuDrawerHiddenWidth !== null &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }
  });

  /** @param {boolean} alwaysSticky */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (alwaysSticky) {
          this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';

          if (this.dataset.themeColor) {
            changeMetaThemeColor(this.dataset.themeColor);
          }
        } else {
          this.#offscreen = !entry.isIntersecting || this.dataset.stickyState === 'active';
        }
      },
      { threshold: alwaysSticky ? 1 : 0 }
    );

    this.#intersectionObserver.observe(this);
  };

  /** @param {OverflowMinimumEvent} event */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /** @param {boolean} hideMenu */
  #updateMenuVisibility(hideMenu) {
    this.#menuDrawerHiddenWidth = hideMenu ? window.innerWidth : null;
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.scrollDirection = isScrollingUp ? 'up' : 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();

    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    if (!document.querySelector('header-component')) {
      document.body.style.setProperty('--header-height', '0px');
    }
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const headerGroup = document.querySelector('#header-group');
  if (!headerGroup) return;

  /** @type {HTMLElement | null} */
  let header = null;

  /** @type {Set<HTMLElement>} */
  let observedElements = new Set();

  const updateHeaderGroupHeight = () => {
    let totalHeight = 0;

    // Measure every observed element. ResizeObserver entries contain only
    // elements that changed, which are not necessarily the whole group.
    for (const element of observedElements) {
      if (
        element !== header ||
        (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
      ) {
        totalHeight += element.offsetHeight;
      }
    }

    document.body.style.setProperty('--header-group-height', `${Math.round(totalHeight)}px`);
  };

  const resizeObserver = new ResizeObserver(updateHeaderGroupHeight);

  const observeCurrentElements = () => {
    const currentHeader = document.querySelector('header-component');
    header = currentHeader instanceof HTMLElement ? currentHeader : null;

    /** @type {Set<HTMLElement>} */
    const nextElements = new Set();

    if (header) nextElements.add(header);

    for (const element of headerGroup.children) {
      if (element instanceof HTMLElement) nextElements.add(element);
    }

    for (const element of observedElements) {
      if (!nextElements.has(element)) resizeObserver.unobserve(element);
    }

    for (const element of nextElements) {
      if (!observedElements.has(element)) resizeObserver.observe(element);
    }

    observedElements = nextElements;
    updateHeaderGroupHeight();
  };

  observeCurrentElements();

  const mutationObserver = new MutationObserver(observeCurrentElements);
  mutationObserver.observe(headerGroup, { childList: true });
});